import { useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconStateNotificationOutline } from "@/assets/icons/figma/IconStateNotificationOutline";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { SaveSearchModal } from "@/features/search/components/saved-search/SaveSearchModal";
import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  buildSavedSearchParams,
  canSaveSearch,
} from "@/features/search/utils/saved-search-params.utils";
import { Button } from "@/ui/cva/Button";
import { Dialog } from "@radix-ui/react-dialog";

/**
 * "Save search" on the results page.
 *
 * Renders nothing when there is nothing to save — an unfiltered, query-less search is the whole
 * catalogue, which the service rejects. Better to have no button than one that always fails.
 *
 * The guest gate runs before the dialog opens rather than on submit: signing up mid-form would
 * throw away whatever was typed into it.
 */
export const SaveSearchButton = () => {
  const { t } = useTranslation();

  const { query } = useSearchContext();
  const router = useRouter();
  const { guestGuard } = useGuestModalGuard();
  const [open, setOpen] = useState(false);

  const params = buildSavedSearchParams(query.data?.meta.rules, router.query);

  if (!canSaveSearch(params)) {
    return null;
  }

  const handleClick = async () => {
    const signedIn = await guestGuard({
      title: t("listing:listing-signup-login-wall-title"),
      description: t("listing:listing-signup-login-wall-desc"),
      referrer: "saved-searches",
    });

    if (!signedIn) {
      return;
    }

    setOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        color="brand"
        size="small"
        disabled={!router.isReady}
        className="whitespace-nowrap"
        leftIcon={<IconStateNotificationOutline className="h-4 w-4 shrink-0" />}
        onClick={handleClick}
      >
        {t("listing:save-search-button")}
      </Button>

      {open && (
        <SaveSearchModal
          params={params}
          onSaved={() => analyticsService.trackSaveAlertList()}
        />
      )}
    </Dialog>
  );
};
